import { Box, styled } from '@mui/material';

import { extractBaseWidgetType, getWidgetPreview } from '../../Settings';
import { useSelectWidgetInstanceEntities } from '../../slices';
import {
  widgetRegistry,
  type WidgetId,
  type WidgetType,
} from '../../widgetManager';

import type { FC } from 'react';

const PREFIX = 'TSMED-DragPreview';

const dragPreviewClasses = {
  root: `${PREFIX}-root`,
};

const Root = styled(Box)(({ theme }) => ({
  [`&.${dragPreviewClasses.root}`]: {
    width: 180, // update value in dragModifiers.ts if changed
    height: 150, // update value in dragModifiers.ts if changed
    backgroundColor: theme.palette.mapBlue['500'],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.spacing(1),
    opacity: 0.9,
    boxShadow: `0 0 15px ${theme.palette.primary.main}60`,
    border: `2px solid ${theme.palette.primary.main}`,
    // NOTE: No transform here - it causes mismatch between visual position and hit detection
    transition: 'none',
    pointerEvents: 'none',
    position: 'relative',
    zIndex: 99999,
    overflow: 'hidden',

    '&::before': {
      content: '""',
      position: 'absolute',
      top: -2,
      left: -2,
      right: -2,
      bottom: -2,
      borderRadius: theme.spacing(1),
      boxShadow: 'none',
      pointerEvents: 'none',
      zIndex: -1,
    },
  },
}));

interface DragPreviewProps {
  id: WidgetId | WidgetType | string;
  type: 'widget-prototype' | 'widget-instance';
  displayName?: string;
}

export const DragPreview: FC<DragPreviewProps> = ({ id, type }) => {
  const widgetInstances = useSelectWidgetInstanceEntities();
  const isPrototype = type === 'widget-prototype';

  let widgetType: WidgetType;
  let variantId: string;

  if (isPrototype) {
    // For palette widgets, use the id directly (could be variant like 'geo-map-center')
    variantId = id as string;
    widgetType = extractBaseWidgetType(variantId);
  } else {
    // For widget instances, get the actual widget type from the instance data
    const widgetInstance = widgetInstances[id as WidgetId];

    if (widgetInstance) {
      widgetType = widgetInstance.type;

      // Special handling for geo-map: determine variant based on section
      if (widgetType === 'geo-map') {
        variantId =
          widgetInstance.sectionId === 'center'
            ? 'geo-map-center'
            : 'geo-map-focus';
      } else {
        variantId = widgetType;
      }
    } else {
      // If widget instance not found, try to infer type from ID prefix
      const idString = id.toString();
      const potentialType = Array.from(widgetRegistry.keys()).find(key =>
        idString.startsWith(key),
      );

      if (potentialType) {
        widgetType = potentialType;
        variantId = potentialType;
      } else {
        // Fallback: shouldn't happen, but return null to avoid errors
        return null;
      }
    }
  }

  return (
    <Root className={dragPreviewClasses.root}>
      {getWidgetPreview(widgetType, variantId)}
    </Root>
  );
};
